import {
  Drawer,
  DrawerActions,
  DrawerContent,
  DrawerHeader
} from '@library/components/navigation';
import type {
  MemberGroup,
  PaymentSettingModifier
} from '@library/types/memberGroups';
import { PrimaryButton, SecondaryButton } from '@library/components/buttons';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  useGetPaymentSettingModifierQuery,
  useUpdatePaymentSettingModifierMutation
} from '@bo-services/members/memberGroupsApi';
import { Loading } from '@library/components/Loading';
import { MemberGroupsContext } from '@bo-hooks/memberGroups/useMemberGroupsContext';
import { NumberInput } from '@library/components/inputs';
import { PrizepicksSwitch } from '@library/components/switches';
import { Spinner } from '@library/components/Spinner';
import StyledModal from '@bo-components/common/StyledModal';
import { usePaymentSettingModifiers } from '@bo-hooks/memberGroups/usePaymentSettingModifierPriorities';

export function PaymentModifierDrawer({
  memberGroup,
  open = false,
  onClose
}: {
  memberGroup: MemberGroup;
  open?: boolean;
  onClose: () => void;
}) {
  const { memberGroups } = useContext(MemberGroupsContext);

  const { priorities, modifiers } = usePaymentSettingModifiers(
    !open || !memberGroup
  );

  const modifierById = useMemo(() => {
    const byId: Record<PaymentSettingModifier['id'], PaymentSettingModifier> =
      {};
    modifiers?.forEach((modifier) => {
      byId[modifier.id] = modifier;
    });
    return byId;
  }, [modifiers]);

  const {
    data: currentPaymentModifier,
    isFetching: isFetchingPaymentModifier
  } = useGetPaymentSettingModifierQuery(
    { memberGroupId: memberGroup?.id ?? 0 },
    { skip: !memberGroup }
  );

  const [
    updatePaymentModifier,
    {
      isError,
      isSuccess,
      isLoading: isUpdatingPaymentModifier,
      reset: resetUpdatePaymentModifier
    }
  ] = useUpdatePaymentSettingModifierMutation();

  const [editingModifier, setEditingModifier] = useState<
    | Pick<
        PaymentSettingModifier,
        'allow_deposits' | 'allow_withdrawals' | 'priority'
      >
    | undefined
  >();

  const [goingToOverride, setGoingToOverride] = useState<
    { name: string; priority: number }[] | undefined
  >();

  useEffect(() => {
    if (currentPaymentModifier) {
      setEditingModifier(currentPaymentModifier);
    } else {
      setEditingModifier({
        allow_deposits: true,
        allow_withdrawals: true,
        priority: 0
      });
    }
  }, [currentPaymentModifier]);

  useEffect(() => {
    if (open) {
      resetUpdatePaymentModifier();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasChanges = useMemo(() => {
    if (!editingModifier) return false;
    if (!currentPaymentModifier) return true;

    return (
      editingModifier.allow_deposits !==
        currentPaymentModifier.allow_deposits ||
      editingModifier.allow_withdrawals !==
        currentPaymentModifier.allow_withdrawals ||
      editingModifier.priority !== currentPaymentModifier.priority
    );
  }, [currentPaymentModifier, editingModifier]);

  const handleChangeAllowDeposits = useCallback(
    (allow_deposits: boolean) => {
      setEditingModifier({
        ...editingModifier!,
        allow_deposits
      });
    },
    [editingModifier]
  );

  const handleChangeAllowWithdrawals = useCallback(
    (allow_withdrawals: boolean) => {
      setEditingModifier({
        ...editingModifier!,
        allow_withdrawals
      });
    },
    [editingModifier]
  );

  const handleChangePriority = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditingModifier({
        ...editingModifier!,
        priority: event.currentTarget.valueAsNumber
      });
    },
    [editingModifier]
  );

  const handleSubmit = useCallback(async () => {
    setGoingToOverride(undefined);

    await updatePaymentModifier({
      memberGroupId: memberGroup.id,
      ...editingModifier!
    });
  }, [editingModifier, memberGroup.id, updatePaymentModifier]);

  const handleCloseOverrideConfirm = useCallback(() => {
    setGoingToOverride(undefined);
  }, []);

  const handleSubmitPreview = useCallback(async () => {
    if (editingModifier!.priority === currentPaymentModifier?.priority) {
      await handleSubmit();
      return;
    }

    const overriding: typeof goingToOverride = [];
    memberGroups!.forEach((group) => {
      if (group.id === memberGroup.id) return;

      const groupPaymentSettingId = group.payment_setting_modifier_id;
      if (typeof groupPaymentSettingId === 'undefined') return;

      const modifier = modifierById[groupPaymentSettingId];
      if (!modifier || editingModifier!.priority < modifier.priority) return;

      overriding.push({
        name: String(group.name),
        priority: modifier.priority
      });
    });

    if (overriding.length > 0) {
      overriding.sort((a, b) => b.priority - a.priority);
      setGoingToOverride(overriding);
    } else {
      await handleSubmit();
    }
  }, [
    currentPaymentModifier,
    editingModifier,
    handleSubmit,
    memberGroup.id,
    memberGroups,
    modifierById
  ]);

  const hasPriorityConflict =
    editingModifier &&
    currentPaymentModifier?.priority !== editingModifier.priority &&
    priorities.has(editingModifier.priority);

  const isLoading = isFetchingPaymentModifier || isUpdatingPaymentModifier;

  const canSubmit = !isLoading && !hasPriorityConflict && hasChanges;

  return (
    <Drawer
      isOpen={open}
      onCloseDrawer={onClose}
      direction="right"
      size="medium"
    >
      <DrawerHeader subtitle={String(memberGroup.name)} onClickClose={onClose}>
        Configure Payment
      </DrawerHeader>

      <DrawerContent>
        {isFetchingPaymentModifier ? (
          <Loading loadingMessage="Loading payment modifier" />
        ) : (
          <>
            <div className="flex justify-between">
              <label htmlFor="allow-deposits">Allow deposits</label>
              <PrizepicksSwitch
                id="allow-deposits"
                testId="allow-deposits"
                active={editingModifier?.allow_deposits ?? false}
                handleChange={handleChangeAllowDeposits}
                disabled={isLoading}
              />
            </div>

            <div className="flex justify-between">
              <label htmlFor="allow-withdrawals">Allow withdrawals</label>
              <PrizepicksSwitch
                id="allow-withdrawals"
                testId="allow-withdrawals"
                active={editingModifier?.allow_withdrawals ?? false}
                handleChange={handleChangeAllowWithdrawals}
                disabled={isLoading}
              />
            </div>

            <div>
              <NumberInput
                id="modifier-prioritiy"
                data-test-id="modifier-prioritiy"
                min={0}
                placeholder="0"
                labelName="Payment modifier priority"
                labelClassName={hasPriorityConflict ? 'text-roses' : ''}
                value={editingModifier?.priority ?? 0}
                onChange={handleChangePriority}
                disabled={isLoading}
              />
              {hasPriorityConflict && (
                <span
                  className="text-roses text-xs"
                  data-test-id="priority-conflict"
                >
                  Priority already used, please pick a different one.
                </span>
              )}
            </div>
          </>
        )}
      </DrawerContent>

      <DrawerActions>
        {isError && (
          <p className="text-roses">Error updating payment modifier</p>
        )}
        {isSuccess && (
          <p className="text-atlien-200">Payment modifier updated</p>
        )}
        <PrimaryButton
          handleOnClick={handleSubmitPreview}
          disabled={isLoading || !canSubmit}
          className="px-3 py-2 text-base font-medium"
        >
          {isUpdatingPaymentModifier ? <Spinner /> : 'Apply'}
        </PrimaryButton>
      </DrawerActions>

      <StyledModal
        isOpen={!!goingToOverride}
        onClose={handleCloseOverrideConfirm}
        bigTitle="Confirm Priority Change"
        testId="override-modal"
        className="p-0"
        actions={
          <>
            <SecondaryButton
              title="Cancel"
              handleOnClick={handleCloseOverrideConfirm}
              testId="cancel-override-button"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              title="Confirm"
              handleOnClick={handleSubmit}
              testId="confirm-override-button"
            >
              Confirm
            </PrimaryButton>
          </>
        }
      >
        <p className="text-soFresh-190">
          Are you sure you want to assign priority{' '}
          <span className="text-soClean-100">{editingModifier?.priority}</span>{' '}
          to member group{' '}
          <span className="text-soClean-100">{memberGroup.name}?</span>
        </p>
        <div>
          <p className="text-soFresh-190">
            This group will have a higher priority than the following groups:
          </p>
          <ul className="list-disc">
            {goingToOverride?.map((override) => (
              <li key={override.name} className="ml-8">
                {override.name}: {override.priority}
              </li>
            ))}
          </ul>
        </div>
      </StyledModal>
    </Drawer>
  );
}
